import { useEffect, useMemo, useState } from 'react';
import { getHotelList, type PmsHotelCacheItem } from '../service/hotelApi';

export interface HotelOption {
  value: number;
  label: string;
  hotelName?: string;
}

export interface UseHotelOptionsParams {
  /**
   * 可选：按品牌过滤（单选）。如果你的表单是多选，这里建议传 undefined 或自行选择一个品牌。
   */
  brandId?: number;
  /**
   * 可选：按大区过滤（单选）
   */
  regionId?: number;
  /**
   * 可选：搜索关键词（按 name/code/fullName 模糊匹配）
   */
  search?: string;
  /**
   * 可选：一次拉取多少条。默认 2000（作为下拉枚举上限）
   */
  pageSize?: number;
  /**
   * 可选：是否显示测试门店（hotelid=1），默认false（不显示）
   */
  showTestStore?: boolean;
}

/**
 * 通用 Hook：门店（酒店）下拉枚举
 * 数据来源：后端缓存表 pms_hotel_cache（GET /api/hotel/list）
 */
export function useHotelOptions(params?: UseHotelOptionsParams) {
  const [hotels, setHotels] = useState<PmsHotelCacheItem[]>([]);
  const [loading, setLoading] = useState(false);

  const query = useMemo(() => {
    return {
      pageNum: 1,
      pageSize: params?.pageSize ?? 2000,
      search: params?.search,
      brandId: params?.brandId,
      regionId: params?.regionId,
    };
  }, [params?.brandId, params?.regionId, params?.search, params?.pageSize]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    getHotelList(query)
      .then((resp) => {
        if (!mounted) return;
        if (resp.code === 1 && resp.data?.list) {
          setHotels(resp.data.list);
        } else {
          setHotels([]);
        }
      })
      .catch(() => {
        if (mounted) setHotels([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [query]);

  const hotelOptions: HotelOption[] = useMemo(() => {
    const list = Array.isArray(hotels) ? hotels : [];
    return list
      .filter((h) => {
        // 基础过滤：必须有id和名称
        if (h?.id === undefined || h?.id === null || !(h.name || h.fullName)) {
          return false;
        }
        // 过滤测试门店（hotelid=1），默认不显示
        if (!params?.showTestStore && h.id === 1) {
          return false;
        }
        return true;
      })
      .map((h) => {
        const name = h.name || h.fullName || `门店 ${h.id}`;
        return {
          value: h.id,
          label: name,
          hotelName: name,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'zh-CN'));
  }, [hotels, params?.showTestStore]);

  return { hotels, hotelOptions, loading };
}

